import { useEffect, useState } from 'react';
import { Card, Row, Col, Badge, Alert, Spinner, Button, Table, Form, Modal } from 'react-bootstrap';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { formatDate } from '../shared/dateFormat';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function StudentTeacherDetail() {
  const { id } = useParams();
  const { user, refresh } = useAuth();
  const [t, setT] = useState(null);
  const [err, setErr] = useState('');
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    setT(null); setErr('');
    api.get(`/student/teachers/${id}`).then(setT).catch((e) => setErr(e.message));
    refresh();
  }, [id, refresh]);

  if (err) return <Alert variant="danger">{err}</Alert>;
  if (!t) return <div className="text-center py-5"><Spinner /></div>;

  const bookingAmount = Number(t.monthly_fee) + Number(t.demo_fee);
  const walletBalance = Number(user?.profile?.wallet_balance ?? 0);

  return (
    <>
      <Button as={Link} to="/student/teachers" variant="link" className="ps-0 mb-2">&larr; Back to browse</Button>
      <Row>
        <Col md={8}>
          <Card className="shadow-sm mb-3">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h3 className="mb-1">{t.name}</h3>
                  <p className="text-muted mb-2">{t.qualifications} · {t.experience_years} years experience · {t.city || 'Online'}{t.locality ? ', ' + t.locality : ''}</p>
                </div>
                <Badge bg="info" className="fs-6">{Number(t.avg_rating).toFixed(1)} ★</Badge>
              </div>
              <p>{t.bio || <span className="text-muted">No bio provided.</span>}</p>

              <h6 className="mt-4">Subjects</h6>
              <div>
                {(t.subjects || []).map((s) => (
                  <Badge key={s.id} bg="light" text="dark" className="me-1 mb-1">
                    {s.name} <span className="text-muted small">({s.expertise_level})</span>
                  </Badge>
                ))}
                {(t.subjects || []).length === 0 && <span className="text-muted small">Not listed.</span>}
              </div>

              <h6 className="mt-4">Weekly availability</h6>
              {t.availability?.length ? (
                <Table size="sm" bordered className="mb-0" style={{ maxWidth: 400 }}>
                  <tbody>{t.availability.map((a, i) => (
                    <tr key={i}><td>{DAYS[a.day_of_week]}</td><td>{(a.start_time || '').slice(0, 5)} – {(a.end_time || '').slice(0, 5)}</td></tr>
                  ))}</tbody>
                </Table>
              ) : <p className="text-muted small">Not listed.</p>}

              <h6 className="mt-4">Recent reviews</h6>
              {t.reviews?.length ? t.reviews.map((r, i) => (
                <div key={i} className="border-bottom pb-2 mb-2">
                  <div><strong>{r.student_name}</strong> <Badge bg="info">{r.rating} ★</Badge> <small className="text-muted">{formatDate(r.created_at)}</small></div>
                  <div className="small">{r.comment}</div>
                </div>
              )) : <p className="text-muted small">No reviews yet.</p>}
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="shadow-sm">
            <Card.Body>
              <Card.Title>Book a demo</Card.Title>
              <p className="mb-1"><strong>Monthly fee:</strong> ₹{Number(t.monthly_fee).toFixed(0)}</p>
              <p className="mb-1"><strong>Demo fee:</strong> ₹{Number(t.demo_fee).toFixed(0)}</p>
              <p className="mb-3"><strong>Total hold:</strong> ₹{bookingAmount.toFixed(0)}</p>
              <Alert variant="info" className="small py-2">
                <strong>₹{bookingAmount.toFixed(0)}</strong> is held from your wallet. <em>Cancel before</em> the
                demo = full refund. <em>Reject after</em> the demo = refund ₹{Number(t.monthly_fee).toFixed(0)}{' '}
                tuition only (₹{Number(t.demo_fee).toFixed(0)} demo fee stays with the platform since the teacher
                conducted the demo).
              </Alert>
              <p className="small mb-1">Wallet balance: <strong>₹{walletBalance.toFixed(2)}</strong></p>
              {walletBalance < bookingAmount ? (
                <>
                  <Alert variant="warning" className="py-2 small">Insufficient balance. Top up ₹{(bookingAmount - walletBalance).toFixed(0)} first.</Alert>
                  <Button as={Link} to="/student/wallet" variant="primary" className="w-100">Top up wallet</Button>
                </>
              ) : (
                <Button variant="primary" className="w-100" onClick={() => setBooking(true)}>Book demo</Button>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {booking && <BookModal teacher={t} bookingAmount={bookingAmount} onClose={() => setBooking(false)} />}
    </>
  );
}

function BookModal({ teacher, bookingAmount, onClose }) {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [slots, setSlots] = useState(null);
  const [chosen, setChosen] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get(`/student/teachers/${teacher.id}/slots`).then(setSlots).catch((e) => setErr(e.message));
  }, [teacher.id]);

  const grouped = groupByDate(slots || []);
  const dates = Object.keys(grouped);
  const [selectedDate, setSelectedDate] = useState('');
  useEffect(() => { if (dates.length && !selectedDate) setSelectedDate(dates[0]); }, [dates, selectedDate]);

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      await api.post('/student/bookings', { teacher_id: teacher.id, demo_slot_at: chosen });
      await refresh();
      navigate('/student/bookings');
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton><Modal.Title>Book demo with {teacher.name}</Modal.Title></Modal.Header>
      <Modal.Body>
        {err && <Alert variant="danger">{err}</Alert>}
        <Alert variant="info" className="small py-2">
          ₹{bookingAmount.toFixed(0)} will be held from your wallet. Cancel before demo = full refund.
          Reject after demo = refund ₹{Number(teacher.monthly_fee).toFixed(0)} tuition only (demo fee kept).
        </Alert>
        {slots === null ? <div className="text-center py-3"><Spinner size="sm" /> Loading slots...</div> :
         slots.length === 0 ? <Alert variant="warning">No open slots in the next 14 days. Please try another teacher or check back later.</Alert> : (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Date</Form.Label>
              <Form.Select value={selectedDate} onChange={(e) => { setSelectedDate(e.target.value); setChosen(''); }}>
                {dates.map((d) => <option key={d} value={d}>{grouped[d][0].day_label} ({grouped[d].length} slot{grouped[d].length === 1 ? '' : 's'})</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Time</Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {(grouped[selectedDate] || []).map((s) => (
                  <Button key={s.datetime} size="sm" variant={chosen === s.datetime ? 'primary' : 'outline-primary'} onClick={() => setChosen(s.datetime)}>
                    {s.start_time} – {s.end_time}
                  </Button>
                ))}
              </div>
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} disabled={busy || !chosen}>{busy ? 'Booking...' : `Confirm — hold ₹${bookingAmount.toFixed(0)}`}</Button>
      </Modal.Footer>
    </Modal>
  );
}

function groupByDate(slots) {
  return slots.reduce((acc, s) => { (acc[s.date] = acc[s.date] || []).push(s); return acc; }, {});
}
